import { nameKey } from './worldFormat'
import { characterClasses } from '../data/guide/classes'
import { sebina, thirdEvolution } from '../data/guide/evolutions'
import { recipes } from '../data/guide/recipes'
import { npcs } from '../data/guide/npcs'
import { buildQuestView, type QuestView } from './questView'
import { npcLocationText, resolveNpcLocation } from './npcLocation'
import type { DropRoute, ItemVariant, Spawn, WorldData } from '../types/world'
import type { CharacterClass } from '../types/progression'

/** Cómo se identificó el objeto. "No identificado" solo tras probar ID, nombre, alias y variantes. */
export type MatchedBy = 'id' | 'nombre' | 'alias' | 'variante'

export type ItemIdentity = { id: string; name: string; matchedBy: MatchedBy; variants: ItemVariant[] }

export type QuestTarget = { monsterId: number; monsterName: string; mapId?: number; mapName?: string; spawns: Spawn[] }
export type QuestRoute = { route: DropRoute; variantName?: string; targets: QuestTarget[] }
export type ItemRoutes = { total: number; disabled: number; quest: QuestRoute[] }

export type NpcPresence = { name: string; spawns: Spawn[] }

type ItemQuery = { itemId?: string; gameName?: string; altNames?: string[] }

/** Cruza las guías de progresión con los datos cargados del servidor. */
export type WorldLookup = {
  identify: (q: ItemQuery) => ItemIdentity | undefined
  routes: (baseId: string) => ItemRoutes
  npc: (monsterId: number) => NpcPresence | undefined
  monster: (name: string) => number | undefined
  map: (name: string) => { id: number; name: string } | undefined
  mapName: (id: number) => string | undefined
  /** Requisitos y objetivos de una misión desde los archivos de misiones, filtrados por clase. */
  quest: (questId: number, cls?: CharacterClass) => QuestView | undefined
  questsLoaded: boolean
}

export function buildLookup(w: WorldData): WorldLookup {
  const itemsByName = new Map<string, string>()
  w.items.forEach((it, id) => {
    const k = nameKey(it.name)
    if (!itemsByName.has(k)) itemsByName.set(k, id)
  })
  const variantsByName = new Map<string, ItemVariant>()
  w.variants.forEach((v) => {
    const k = nameKey(v.name)
    if (!variantsByName.has(k)) variantsByName.set(k, v)
  })
  const monsters = new Map<string, number>()
  w.monsters.forEach((m) => {
    const k = nameKey(m.name)
    if (!monsters.has(k)) monsters.set(k, m.id)
  })
  const mapList = Array.from(w.maps.values())
  const maps = new Map<string, { id: number; name: string }>()
  mapList.forEach((m) => {
    const k = nameKey(m.name)
    if (!maps.has(k)) maps.set(k, { id: m.id, name: m.name })
  })

  const identity = (id: string, matchedBy: MatchedBy): ItemIdentity | undefined => {
    const it = w.items.get(id)
    if (!it) {
      // Variante exacta (p. ej. 13,31@0): se conserva su propio ID y nombre, sin fusionarla con el ítem base.
      const v = w.variants.get(id)
      if (v && v.id !== v.itemId) return { id: v.id, name: v.name, matchedBy, variants: [] }
      return undefined
    }
    return { id, name: it.name, matchedBy, variants: (w.idx.variantsByItem.get(id) ?? []).filter((v) => v.id !== id) }
  }

  return {
    identify: ({ itemId, gameName, altNames = [] }) => {
      if (itemId) {
        const byId = identity(itemId, 'id')
        if (byId) return byId
      }
      if (gameName) {
        const id = itemsByName.get(nameKey(gameName))
        if (id) return identity(id, 'nombre')
      }
      for (const n of altNames) {
        const id = itemsByName.get(nameKey(n))
        if (id) return identity(id, 'alias')
      }
      for (const n of [gameName, ...altNames]) {
        if (!n) continue
        const v = variantsByName.get(nameKey(n))
        if (v) return identity(v.itemId, 'variante')
      }
      return undefined
    },
    routes: (baseId) => {
      const exactVariant = w.variants.get(baseId)
      const variants = exactVariant && exactVariant.id !== exactVariant.itemId ? [exactVariant] : w.idx.variantsByItem.get(baseId) ?? []
      const seen = new Set<string>()
      const all: DropRoute[] = []
      const push = (list?: DropRoute[]) =>
        list?.forEach((r) => {
          if (seen.has(r.id)) return
          seen.add(r.id)
          all.push(r)
        })
      push(w.idx.routesByItem.get(baseId))
      variants.forEach((v) => push(w.idx.routesByVariant.get(v.id)))
      const quest = all
        .filter((r) => r.kind === 'quest')
        .map((route) => {
          const pairs = new Map<string, QuestTarget>()
          ;(w.idx.routeTargetsByRoute.get(route.id) ?? []).forEach((t) => {
            if (t.monsterId === undefined) return
            const k = `${t.monsterId}|${t.mapId ?? ''}`
            if (pairs.has(k)) return
            const spawns = (w.idx.spawnsByMonster.get(t.monsterId) ?? []).filter((s) => t.mapId === undefined || s.mapId === t.mapId)
            pairs.set(k, {
              monsterId: t.monsterId,
              monsterName: w.monsters.get(t.monsterId)?.name ?? `Monstruo ${t.monsterId}`,
              mapId: t.mapId,
              mapName: t.mapId !== undefined ? w.maps.get(t.mapId)?.name : undefined,
              spawns,
            })
          })
          return { route, variantName: route.variantId ? w.variants.get(route.variantId)?.name : undefined, targets: Array.from(pairs.values()) }
        })
      return { total: all.length, disabled: all.filter((r) => r.verifiedPossible === false).length, quest }
    },
    npc: (monsterId) => {
      const m = w.monsters.get(monsterId)
      if (!m) return undefined
      return { name: m.name, spawns: w.idx.spawnsByMonster.get(monsterId) ?? [] }
    },
    monster: (name) => monsters.get(nameKey(name)),
    mapName: (id) => w.maps.get(id)?.name,
    quest: (questId, cls) => buildQuestView(w, questId, cls),
    questsLoaded: w.quests.rules.length > 0,
    map: (name) => {
      const k = nameKey(name)
      const exact = maps.get(k)
      if (exact) return exact
      // Nombre parcial (p. ej. "Barracks" frente a "Barracks of Balgass"): solo si hay un único candidato.
      const partial = mapList.filter((m) => nameKey(m.name).split(' ').includes(k))
      return partial.length === 1 ? { id: partial[0].id, name: partial[0].name } : undefined
    },
  }
}

type Status = 'identificado' | 'documentado' | 'pendiente'
export type MaterialsSummary = { identity: { status: Status; detail: string }; routes: { status: Status; detail: string } }

/** Estado de identidad y de obtención de un grupo de materiales, calculado con los datos (nunca supuesto). */
export function summarizeMaterials(l: WorldLookup | undefined, mats: ItemQuery[]): MaterialsSummary | undefined {
  if (!l) return undefined
  const ids = mats.map((m) => ({ m, hit: l.identify(m) }))
  const found = ids.filter((x) => x.hit)
  const missing = ids.filter((x) => !x.hit).map((x) => x.m.gameName ?? x.m.itemId)
  const withQuest = found.filter((x) => l.routes(x.hit!.id).quest.length > 0)
  const withAny = found.filter((x) => l.routes(x.hit!.id).total > 0)
  return {
    identity: {
      status: missing.length ? 'pendiente' : 'identificado',
      detail: `${found.length} de ${ids.length} identificados en el catálogo${missing.length ? ` · no identificados tras revisar ID, nombre, alias y variantes: ${missing.join(', ')}` : ''}`,
    },
    routes: {
      status: withAny.length === ids.length && ids.length > 0 ? 'documentado' : 'pendiente',
      detail: `${withQuest.length} con ruta de misión (requiere la misión activa) · ${withAny.length} de ${ids.length} con alguna ruta de obtención`,
    },
  }
}

export type NameCheck = { kind: 'Ítem' | 'Monstruo' | 'Mapa' | 'NPC'; name: string; where: string; found?: string; detail?: string }

/** Panel técnico: todo lo que usan las guías de progresión frente a los datos cargados. */
export function checkProgressionNames(w: WorldData): NameCheck[] {
  const l = buildLookup(w)
  const out: NameCheck[] = []
  const item = (q: ItemQuery, where: string) => {
    const hit = l.identify(q)
    const routes = hit ? l.routes(hit.id) : undefined
    out.push({
      kind: 'Ítem',
      name: [q.itemId, q.gameName, ...(q.altNames ?? [])].filter(Boolean).join(' / '),
      where,
      found: hit ? `${hit.name} (${hit.id}) · por ${hit.matchedBy}` : undefined,
      detail: hit
        ? `${hit.variants.length} variantes · ${routes!.total} rutas (${routes!.disabled} desactivadas) · ${routes!.quest.length} rutas quest con ${routes!.quest.reduce((n, q) => n + q.targets.length, 0)} pares monstruo/mapa`
        : 'Revisado: ID, nombre, nombres alternativos y nombres de variantes',
    })
  }
  const mon = (name: string, where: string) => {
    const id = l.monster(name)
    out.push({ kind: 'Monstruo', name, where, found: id !== undefined ? `id ${id}` : undefined })
  }
  const map = (name: string, where: string) => {
    const m = l.map(name)
    out.push({ kind: 'Mapa', name, where, found: m ? `${m.name} (id ${m.id})` : undefined })
  }

  item(sebina.scroll, 'Sebina')
  characterClasses.forEach((c) => c.sebinaItem && item(c.sebinaItem, `Sebina · ${c.base}`))
  thirdEvolution.stage1.materials.forEach((m) => item(m, 'Tercera evolución'))
  map(thirdEvolution.stage2.map, 'Tercera evolución')
  map(thirdEvolution.stage3.map, 'Tercera evolución')
  ;['Balram', 'Death Spirit', 'Soram', 'Dark Elf'].forEach((n) => mon(n, 'Tercera evolución (objetivos de quest_objectives)'))
  npcs.forEach((n) => {
    const p = n.monsterId !== undefined ? l.npc(n.monsterId) : undefined
    out.push({
      kind: 'NPC',
      name: `${n.name} (id ${n.monsterId ?? '—'})`,
      where: n.role,
      found: p ? p.name : undefined,
      detail: npcLocationText(resolveNpcLocation(n, { status: 'ready', data: w })),
    })
  })
  recipes.forEach((r) => {
    const ing = [...r.ingredients, ...(r.variants ?? []).flatMap((v) => v.ingredients)]
    ing.filter((m) => m.gameName || m.itemId).forEach((m) => item(m, `Receta ${r.shortTitle}`))
    const res = [...r.results, ...(r.variants ?? []).flatMap((v) => v.results)]
    res.forEach((x) => item(x, `Resultado ${r.shortTitle}`))
  })
  const seen = new Set<string>()
  return out.filter((c) => {
    const k = `${c.kind}|${c.name}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}
